import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Settings, X } from 'lucide-react';
import { Alert } from '../../store/alertStore';
import { roundToTick } from '../../config/instruments';
import './AlertLine.css';

export interface AlertLineProps {
  alert: Alert;
  yPosition: number;
  symbol: string;
  onCoordinateToPrice: (yCoordinate: number) => number | null;
  onUpdate: (id: string, price: number) => void;
  onDelete: (id: string) => void;
  onOpenSettings: (alert: Alert) => void;
}


const AlertLine: React.FC<AlertLineProps> = ({
  alert,
  yPosition,
  symbol,
  onCoordinateToPrice,
  onUpdate,
  onDelete,
  onOpenSettings,
}) => {
  const lineElRef = useRef<HTMLDivElement>(null);
  const controlsElRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isPendingUpdate, setIsPendingUpdate] = useState(false);
  const alertPrice = alert.price || 0;
  const [dragPrice, setDragPrice] = useState<number>(alertPrice);
  const [dragY, setDragY] = useState<number>(yPosition);

  // Set position imperatively — initial mount and when yPosition prop changes (alert moved).
  // During drag/pending, position is managed by drag handlers directly.
  useLayoutEffect(() => {
    if (isDragging || isPendingUpdate) return;
    if (lineElRef.current) lineElRef.current.style.top = `${yPosition}px`;
    if (controlsElRef.current) controlsElRef.current.style.top = `${yPosition}px`;
  }, [yPosition, isDragging, isPendingUpdate]);
  const isPriceAbove = alert.alertType === 'price_above';
  const isPriceBelow = alert.alertType === 'price_below';
  const isVolumeAlert = alert.alertType === 'volume_spike' || alert.alertType === 'volume_above';

  // Reset drag state when alert price changes externally (update completed)
  useEffect(() => {
    if (!isDragging) {
      setDragPrice(alertPrice);
      // If we were pending an update, the price change means update completed
      if (isPendingUpdate) {
        setIsPendingUpdate(false);
      }
    }
  }, [alertPrice, isDragging, isPendingUpdate]);

  // Fallback: Clear pending state after timeout (in case update fails silently)
  useEffect(() => {
    if (isPendingUpdate) {
      const timeout = setTimeout(() => {
        setIsPendingUpdate(false);
      }, 3000); // 3 second timeout
      return () => clearTimeout(timeout);
    }
  }, [isPendingUpdate]);

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    // Don't start drag if clicking on a button
    if ((e.target as HTMLElement).tagName === 'BUTTON') {
      return;
    }

    // Don't allow dragging volume alerts (they don't have a price)
    if (isVolumeAlert) {
      return;
    }

    e.stopPropagation();
    if ('preventDefault' in e && e.type !== 'touchstart') {
      e.preventDefault();
    }
    setIsDragging(true);
    setDragPrice(alertPrice);
    setDragY(yPosition);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      const chartContainer = document.querySelector('.chart-container');
      if (!chartContainer) return;

      const rect = chartContainer.getBoundingClientRect();
      const relativeY = e.clientY - rect.top;

      setDragY(relativeY);
      if (lineElRef.current) lineElRef.current.style.top = `${relativeY}px`;
      if (controlsElRef.current) controlsElRef.current.style.top = `${relativeY}px`;

      const newPrice = onCoordinateToPrice(relativeY);
      if (newPrice !== null) {
        const snappedPrice = roundToTick(newPrice, symbol);
        setDragPrice(snappedPrice);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;

      e.preventDefault();

      const chartContainer = document.querySelector('.chart-container');
      if (!chartContainer) return;

      const rect = chartContainer.getBoundingClientRect();
      const touch = e.touches[0];
      const relativeY = touch.clientY - rect.top;

      setDragY(relativeY);
      if (lineElRef.current) lineElRef.current.style.top = `${relativeY}px`;
      if (controlsElRef.current) controlsElRef.current.style.top = `${relativeY}px`;

      const newPrice = onCoordinateToPrice(relativeY);
      if (newPrice !== null) {
        const snappedPrice = roundToTick(newPrice, symbol);
        setDragPrice(snappedPrice);
      }
    };

    const handleMouseUp = () => {
      if (isDragging && dragPrice !== alertPrice) {
        setIsPendingUpdate(true);
        onUpdate(alert.id, dragPrice);
      }
      setIsDragging(false);
    };

    const handleTouchEnd = () => {
      if (isDragging && dragPrice !== alertPrice) {
        setIsPendingUpdate(true);
        onUpdate(alert.id, dragPrice);
      }
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, dragPrice, alert.id, alertPrice, symbol, onCoordinateToPrice, onUpdate]);

  const displayPrice = (isDragging || isPendingUpdate) ? dragPrice : alertPrice;

  const getAlertTypeClass = () => {
    if (isPriceAbove) return 'price-above';
    if (isPriceBelow) return 'price-below';
    return 'volume';
  };

  return (
    <>
      {/* Horizontal dashed line extending to the left */}
      <div
        ref={lineElRef}
        className={`alert-line ${getAlertTypeClass()} ${isDragging ? 'dragging' : ''} ${alert.paused ? 'paused' : ''}`}
        style={{
          right: '80px',
        }}
      />
      {/* Minimal control label */}
      <div
        ref={controlsElRef}
        className={`alert-line-controls ${getAlertTypeClass()} ${isDragging ? 'dragging' : ''}`}
        style={{
          right: '80px',
          transform: 'translateY(-50%)',
          cursor: isVolumeAlert ? 'default' : (isDragging ? 'grabbing' : 'grab'),
        }}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        {!isVolumeAlert && (
          <span className="alert-price">{displayPrice.toFixed(2)}</span>
        )}
        {isVolumeAlert && alert.alertType === 'volume_spike' && alert.volumeMultiplier && (
          <span className="alert-price">{alert.volumeMultiplier.toFixed(1)}x</span>
        )}
        {isVolumeAlert && alert.alertType === 'volume_above' && alert.volumeThreshold != null && (
          <span className="alert-price">{Math.round(alert.volumeThreshold).toLocaleString()}</span>
        )}
        <button
          className="alert-action-btn settings-btn"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onOpenSettings(alert);
          }}
          title="Alert settings"
        >
          <Settings size={10} />
        </button>
        <button
          className="alert-action-btn delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onDelete(alert.id);
          }}
          title="Delete alert"
        >
          <X size={10} />
        </button>
      </div>
    </>
  );
};

export default AlertLine;
